import { Box, Flex, HStack, Icon, Image, Text } from "@chakra-ui/react";
import { FaTag } from "react-icons/fa";

const LPN_HOLO_SRC = "/images/LPN_HOLO.png";

type GaraazOrderVehicleInfoStripProps = {
  orderData: {
    licensePlateNumber?: string | null;
    carModel?: string | null;
    vinNumber?: string | null;
    enquiryTag?: string | null;
  };
};

/** Spaces an Indian LPN for display (same idea as mobile formatIndianPlate). */
export function formatLpnForDisplay(plate: string): string {
  const p = plate.trim().toUpperCase().replace(/[\s-]+/g, "");
  if (p.length >= 10) {
    return `${p.slice(0, 2)} ${p.slice(2, 4)} ${p.slice(4, 6)} ${p.slice(6)}`;
  }
  if (p.length >= 6) {
    return `${p.slice(0, 2)} ${p.slice(2, 4)} ${p.slice(4)}`;
  }
  return p;
}

/**
 * Show model name only — strip trailing " - Petrol - 2023" style fuel/year suffixes.
 */
export function formatCarModelForVehicleStrip(carModel: string): string {
  return carModel
    .replace(
      /\s*-\s*(Petrol|Diesel|CNG|Electric|Hybrid|LPG)\s*-\s*\d{4}\s*$/i,
      "",
    )
    .trim();
}

export function hasVehicleInfoStripData(orderData: {
  licensePlateNumber?: string | null;
  carModel?: string | null;
  vinNumber?: string | null;
  enquiryTag?: string | null;
}): boolean {
  const plate = orderData?.licensePlateNumber?.trim();
  const model = orderData?.carModel
    ? formatCarModelForVehicleStrip(orderData.carModel)
    : "";
  const vin = orderData?.vinNumber?.trim();
  const tag = orderData?.enquiryTag?.trim();
  return !!(plate || model || vin || tag);
}

/** VIN with last 6 digits bold only (no yellow background). */
const VinWithLast6Bold = ({ vin }: { vin: string }) => {
  const upper = vin.trim().toUpperCase();
  if (!upper) return null;
  const highlightLen = 6;
  const prefix =
    upper.length > highlightLen ? upper.slice(0, -highlightLen) : "";
  const last6 =
    upper.length > highlightLen ? upper.slice(-highlightLen) : upper;

  return (
    <Text
      as="span"
      fontSize="xs"
      color="gray.600"
      fontFamily="mono"
      whiteSpace="nowrap"
      flexShrink={0}
      noOfLines={1}
    >
      {prefix}
      <Text as="span" fontWeight="extrabold" color="#111" letterSpacing="0.02em">
        {last6}
      </Text>
    </Text>
  );
};

/**
 * Mirrors mobile `IndianNumberPlate` styles (LPN_HOLO + IND strip + plate text).
 */
const IndianNumberPlate = ({ number }: { number: string }) => {
  const formatted = formatLpnForDisplay(number);
  if (!formatted) return null;

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="stretch"
      alignSelf="flex-start"
      bg="#FFFFFF"
      borderWidth="1.5px"
      borderStyle="solid"
      borderColor="#111111"
      borderRadius="3px"
      overflow="hidden"
      flexShrink={0}
      maxW="100%"
      gap="0"
      aria-label={`Number plate ${formatted}`}
    >
      {/* styles.indStrip */}
      <Box
        w="24px"
        minW="24px"
        bg="#1D4ED9"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        pt="4px"
        pb="3px"
        flexShrink={0}
      >
        <Image
          src={LPN_HOLO_SRC}
          alt=""
          w="12px"
          h="12px"
          objectFit="contain"
          display="block"
          aria-hidden
        />
        <Text
          color="#FFFFFF"
          fontSize="10px"
          fontWeight="800"
          letterSpacing="0.5px"
          textAlign="center"
          lineHeight="12px"
          m={0}
        >
          IND
        </Text>
      </Box>
      {/* styles.numberWrap — lighter weight than IND so plate digits don't overpower */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        px="6px"
        py="2px"
        bg="#FFFFFF"
      >
        <Text
          color="#111111"
          fontSize="16px"
          fontWeight="600"
          fontFamily="'Courier New', Courier, monospace"
          letterSpacing="1px"
          whiteSpace="nowrap"
          lineHeight="1.15"
          m={0}
        >
          {formatted}
        </Text>
      </Box>
    </Box>
  );
};

const GaraazOrderVehicleInfoStrip: React.FC<
  GaraazOrderVehicleInfoStripProps
> = ({ orderData }) => {
  if (!hasVehicleInfoStripData(orderData)) {
    return null;
  }

  const plate = orderData.licensePlateNumber?.trim();
  const model = orderData.carModel
    ? formatCarModelForVehicleStrip(orderData.carModel)
    : "";
  const vin = orderData.vinNumber?.trim();
  const tag = orderData.enquiryTag?.trim();

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={3}
      px="0.5rem"
      py="0.4rem"
      bg="white"
      borderTop="1px solid"
      borderColor="blackAlpha.100"
      flexShrink={0}
      flexWrap="wrap"
      w="100%"
      minW={0}
    >
      <HStack spacing={3} alignItems="center" minW={0} flex={1}>
        {plate && <IndianNumberPlate number={plate} />}

        {(model || vin) && (
          <HStack spacing={2} alignItems="baseline" minW={0} flex={1}>
            {model && (
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color="gray.800"
                noOfLines={1}
              >
                {model}
              </Text>
            )}
            {vin && <VinWithLast6Bold vin={vin} />}
          </HStack>
        )}
      </HStack>

      {tag && (
        <HStack
          spacing="6px"
          alignItems="center"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="#9CA3AF"
          borderRadius="8px"
          bg="#F5F4FA"
          px="10px"
          py="6px"
          minW={0}
          maxW={{ base: "100%", md: "45%" }}
          flexShrink={1}
          ml="auto"
          aria-label={`Tag: ${tag}`}
        >
          <Icon as={FaTag} color="#3F3D8F" boxSize="14px" flexShrink={0} />
          <Text
            fontSize="12px"
            color="#3F3D8F"
            noOfLines={1}
            minW={0}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            <Text as="span" fontWeight="700" color="#3F3D8F">
              Tag:{" "}
            </Text>
            {tag}
          </Text>
        </HStack>
      )}
    </Flex>
  );
};

export default GaraazOrderVehicleInfoStrip;
